import React, { useState, useEffect, useRef, useContext } from 'react';
import CloudUpload from '@mui/icons-material/CloudUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { STATIC_ICONS } from '../Constants';
import { LanguageContext } from '../context/LanguageContext';
import commonTranslations from '../languages/common.json';


const getPreviewUrl = (file, initialUrl, isDeleted) => {
  if (file instanceof Blob) {
    return URL.createObjectURL(file);
  }

  if (typeof file === 'string' && file.trim().length > 0) {
    const normalized = file.trim();
    const isAbsolute = /^blob:|^data:|^https?:/i.test(normalized);
    return isAbsolute ? normalized : (STATIC_ICONS + normalized);
  }

  if (initialUrl && !isDeleted) {
    const normalized = String(initialUrl).trim();
    const isAbsolute = /^blob:|^data:|^https?:/i.test(normalized);
    return isAbsolute ? normalized : (STATIC_ICONS + normalized);
  }

  return null;
};

const BackgroundImageDropzone = ({
  title,
  file,
  initialUrl,
  isDeleted,
  onFileChange,
  onDelete,
  t,
}) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const nextPreviewUrl = getPreviewUrl(file, initialUrl, isDeleted);
    setPreviewUrl(nextPreviewUrl);

    return () => {
      if (nextPreviewUrl && nextPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(nextPreviewUrl);
      }
    };
  }, [file, initialUrl, isDeleted]);

  return (
    <div className="edit-content-part" style={{ flex: 1, minWidth: 220 }}>
      <label>{title}</label>
      <div
        className="edit-form-item"
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: 210,
        }}
      >
        <input
          type="file"
          accept="image/*"
          className="edit-logo-section"
          title=""
          onChange={onFileChange}
        />

        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt=""
              onClick={onDelete}
              style={{ cursor: 'pointer', width: '100%', maxHeight: '200px', objectFit: 'contain' }}
            />
            <DeleteIcon onClick={onDelete} style={{ cursor: 'pointer' }} />
          </>
        ) : (
          <>
            <CloudUpload
              style={{
                position: 'absolute',
                top: '15%',
                pointerEvents: 'none',
                color: 'var(--accent)',
                transform: 'scale(2.5)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                pointerEvents: 'none',
                fontWeight: 500,
              }}
            >
              {t.uploadText}
            </div>
            <div
              style={{
                position: 'absolute',
                left: '5%',
                right: '5%',
                bottom: '15%',
                pointerEvents: 'none',
                textAlign: 'center',
              }}
              className="edit-helper"
            >
              {t.uploadHelper}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const BackgroundImageSettings = ({
  onFileChanged,
  initialFile = null,
  initialUrl = null,
  initialLightFile = null,
  initialLightUrl = null,
  initialDarkFile = null,
  initialDarkUrl = null,
}) => {
  const { lang } = useContext(LanguageContext);
  const t = commonTranslations[lang].common.backgroundImageSettings;

  const legacyFile = initialFile || null;
  const legacyUrl = initialUrl || null;
  const [lightFile, setLightFile] = useState(initialLightFile || legacyFile);
  const [darkFile, setDarkFile] = useState(initialDarkFile || legacyFile);
  const [isLightDeleted, setIsLightDeleted] = useState(false);
  const [isDarkDeleted, setIsDarkDeleted] = useState(false);
  const onFileChangedRef = useRef(onFileChanged);
  const hasUserChangedRef = useRef(false);

  useEffect(() => {
    onFileChangedRef.current = onFileChanged;
  }, [onFileChanged]);

  useEffect(() => {
    if (!hasUserChangedRef.current) return;

    if (typeof onFileChangedRef.current === 'function') {
      onFileChangedRef.current({
        lightFile: lightFile instanceof Blob ? lightFile : null,
        darkFile: darkFile instanceof Blob ? darkFile : null,
        lightDeleted: isLightDeleted,
        darkDeleted: isDarkDeleted,
      });
    }
  }, [lightFile, darkFile, isLightDeleted, isDarkDeleted]);

  const handleLightFileChange = (e) => {
    if (e.target.files.length > 0) {
      hasUserChangedRef.current = true;
      setLightFile(e.target.files[0]);
      setIsLightDeleted(false);
    }
  };

  const handleDarkFileChange = (e) => {
    if (e.target.files.length > 0) {
      hasUserChangedRef.current = true;
      setDarkFile(e.target.files[0]);
      setIsDarkDeleted(false);
    }
  };

  const handleLightDelete = () => {
    hasUserChangedRef.current = true;
    setLightFile(null);
    setIsLightDeleted(true);
  };

  const handleDarkDelete = () => {
    hasUserChangedRef.current = true;
    setDarkFile(null);
    setIsDarkDeleted(true);
  };

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', width: '100%' }}>
      <BackgroundImageDropzone
        title={t.lightLabel || 'Light mode image'}
        file={lightFile}
        initialUrl={initialLightUrl || legacyUrl}
        isDeleted={isLightDeleted}
        onFileChange={handleLightFileChange}
        onDelete={handleLightDelete}
        t={t}
      />
      <BackgroundImageDropzone
        title={t.darkLabel || 'Dark mode image'}
        file={darkFile}
        initialUrl={initialDarkUrl || legacyUrl}
        isDeleted={isDarkDeleted}
        onFileChange={handleDarkFileChange}
        onDelete={handleDarkDelete}
        t={t}
      />
    </div>
  );
};

export default BackgroundImageSettings;
